'use client';

/* ──────────────────────────────────────────────
 *  Récap de la confirmation (spec Parcours P3 § 7.7, planche 8) :
 *  date et heure, lieu avec itinéraire, membre, puis le même récap
 *  que le tunnel (`TunnelSummary`) : prestations avec formule, durée,
 *  prix normal et suppléments, remise, total après remise, carte
 *  cadeau, payé en ligne, reste sur place — le client qui additionne
 *  retrouve le total (§ 9). Économie réalisée, puis « Ajouter au
 *  calendrier ». Montants et durées formatés serveur.
 * ────────────────────────────────────────────── */

import { type JSX, useState } from 'react';
import { ProPaymentStatus, type BookingConfirmationResponseObject } from '@cfixe-monorepo/shared';
import type { BookingLabels } from '../../types';
import { discountRowsOf, voucherRowsOf } from '../../utils/amount-lines';
import { bookedLineLabel, lineDetailSublines, recapLinePrice } from '../../utils/recap-lines';
import { computeSummaryStruckTotal } from '../../utils/summary-total';
import {
  TunnelSummary,
  type TunnelSummaryItem,
  type TunnelSummaryPaymentRow,
  type TunnelSummarySupplement,
} from './TunnelSummary';

/** Lien « Itinéraire » : recherche de l'adresse dans Google Maps (aucune clé requise). */
const itineraryUrl = (address: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export function BookingConfirmationRecap({
  booking,
  labels,
  icsUrl,
}: {
  booking: BookingConfirmationResponseObject;
  labels: BookingLabels;
  /** Fichier .ics servi par le backend (`GET public/booking/:uuid/calendar.ics`). */
  icsUrl: string;
}): JSX.Element {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const amounts = booking.amounts;
  const slots = booking.slots ?? [];
  const first = slots[0];
  const address = first?.customer_address_formatted ?? first?.pro_place_address_formatted;
  const memberNames = [
    ...new Set(
      slots.flatMap((slot): string[] =>
        slot.team_members?.length
          ? slot.team_members.map((member): string => member.firstname ?? '')
          : slot.team_member_firstname
            ? [slot.team_member_firstname]
            : [],
      ),
    ),
  ].filter(Boolean);
  // Débité en ligne : seulement un paiement réglé (une empreinte ou une carte enregistrée ne débite rien).
  const paidOnline =
    booking.payment_status === ProPaymentStatus.PAYED && (amounts?.amount_to_pay_today_raw ?? 0) > 0
      ? amounts?.amount_to_pay_today
      : undefined;
  const remaining = (amounts?.remaining_on_site_raw ?? 0) > 0 ? amounts?.remaining_on_site : undefined;

  /* Lignes au prix normal, comme le récap du tunnel : formule et durée en sous-ligne, suppléments chiffrés. */
  const items: TunnelSummaryItem[] = slots.flatMap((slot): TunnelSummaryItem[] =>
    slot.prestations.map(
      (prestation): TunnelSummaryItem => ({
        label: bookedLineLabel(prestation, labels.party_size_suffix),
        price: recapLinePrice(prestation.price_type, prestation.price, labels),
        sublines: lineDetailSublines(prestation),
        // Quantité renseignée par le serveur au-delà d'un seul exemplaire du supplément (« × 2 »).
        supplements: (prestation.supplements ?? []).map(
          (supplement): TunnelSummarySupplement => ({
            name: supplement.name,
            price: supplement.price,
            quantity: supplement.quantity,
          }),
        ),
      }),
    ),
  );
  /* Après le total : carte cadeau ou bon (ce qu'il a payé), payé en ligne, reste à régler sur place. */
  const paymentRows: TunnelSummaryPaymentRow[] = [
    ...(paidOnline ? [{ label: labels.paid_online, value: paidOnline }] : []),
    ...(remaining ? [{ label: labels.remaining_on_site, value: remaining, highlight: true }] : []),
  ];

  return (
    <div className="bt-confirmation-recap">
      {booking.customer_email ? (
        <p className="bt-confirmation-recap__email">
          {labels.confirmation_email_sent.replace('{email}', booking.customer_email)}
        </p>
      ) : null}

      <div className="bt-confirmation-recap__card">
        {slots.map(
          (slot, index): JSX.Element => (
            <p key={`when-${index}`} className="bt-confirmation-recap__when">
              {slot.start_formatted}
            </p>
          ),
        )}
        {first?.pro_place_label || address ? (
          <p className="bt-confirmation-recap__row">
            <span>{[first?.pro_place_label, address].filter(Boolean).join(' · ')}</span>
            {address ? (
              <a className="bt-link-subtle" href={itineraryUrl(address)} target="_blank" rel="noopener noreferrer">
                {labels.itinerary}
              </a>
            ) : null}
          </p>
        ) : null}
        {memberNames.length > 0 ? (
          <p className="bt-confirmation-recap__row">{labels.with_member.replace('{name}', memberNames.join(', '))}</p>
        ) : null}

        <div className="bt-summary__divider" />
        <TunnelSummary
          variant="sheet"
          items={items}
          // Serveur antérieur au bloc `amounts` : le total de la réservation, sans détail.
          totalValue={amounts?.net_total ?? booking.payment_infos?.total_amount ?? ''}
          totalStruck={computeSummaryStruckTotal(amounts)}
          discountRows={discountRowsOf(amounts, labels)}
          voucherRows={voucherRowsOf(amounts, labels)}
          paymentRows={paymentRows}
          labels={{ selection_title: labels.summary_title_single, total_label: labels.total_label }}
        />
        {amounts?.savings ? (
          <p className="bt-confirmation-recap__savings">{labels.savings.replace('{amount}', amounts.savings)}</p>
        ) : null}
      </div>

      <div className="bt-confirmation-recap__calendar">
        <button
          type="button"
          className="bt-cta bt-cta--secondary"
          aria-expanded={calendarOpen}
          onClick={(): void => setCalendarOpen((open): boolean => !open)}
        >
          {labels.add_to_calendar}
        </button>
        {calendarOpen ? (
          <div className="bt-confirmation-recap__calendar-options">
            {booking.calendar_google_url ? (
              <a className="bt-chip" href={booking.calendar_google_url} target="_blank" rel="noopener noreferrer">
                {labels.add_to_google_calendar}
              </a>
            ) : null}
            <a className="bt-chip" href={icsUrl} download>
              {labels.add_to_ics_calendar}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
